<script setup>
/**
 * Aviso: este contato tem OUTRA conversa não resolvida na mesma caixa.
 *
 * Rede de segurança do problema de conversas duplicadas. Não muda
 * comportamento nenhum — só torna visível para a atendente que existe outra
 * conversa aberta com a mesma pessoa, com link para ela.
 *
 * Vale mesmo com o roteamento de entrada ativo (kanban_incoming_conversation_routing):
 * cobre os casos históricos, que já nasceram duplicados, e protege quando o
 * roteamento escolhe um destino diferente do que a atendente esperava — o
 * WhatsApp tem uma thread por pessoa, então nenhuma regra acerta sempre.
 *
 * Componente próprio para manter a alteração no MessagesView nativo em uma
 * única linha.
 */
import { ref, computed, watch } from 'vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import ContactAPI from 'dashboard/api/contacts';
import { frontendURL } from 'dashboard/helper/URLHelper';

const props = defineProps({
  conversationId: { type: [Number, String], default: null },
  contactId: { type: [Number, String], default: null },
  inboxId: { type: [Number, String], default: null },
  accountId: { type: [Number, String], default: null },
});

const others = ref([]);

// 1 = resolved (Conversation.statuses no backend). Pending e snoozed também
// estão em aberto do ponto de vista de quem espera resposta, então contam.
const isUnresolved = status => status !== 'resolved';

const fetchOthers = async () => {
  others.value = [];
  if (!props.contactId || !props.conversationId) return;

  try {
    const { data } = await ContactAPI.getConversations(props.contactId);
    const list = data?.payload ?? [];
    others.value = list.filter(
      c =>
        Number(c.id) !== Number(props.conversationId) &&
        isUnresolved(c.status) &&
        // só a MESMA caixa: conversa do mesmo contato noutro canal não é duplicata
        (!props.inboxId || Number(c.inbox_id) === Number(props.inboxId))
    );
  } catch {
    // Aviso é acessório: se a busca falhar, a tela segue normal e silenciosa.
    others.value = [];
  }
};

watch(
  () => [props.conversationId, props.contactId],
  () => fetchOthers(),
  { immediate: true }
);

const hasOthers = computed(() => others.value.length > 0);

const message = computed(() => {
  const n = others.value.length;
  return n === 1
    ? 'Este contato tem outra conversa aberta.'
    : `Este contato tem outras ${n} conversas abertas.`;
});

// Link para a mais recente: é para onde o roteamento de entrada entrega.
const target = computed(() =>
  others.value.length
    ? [...others.value].sort((a, b) => Number(b.id) - Number(a.id))[0]
    : null
);

const linkText = computed(() =>
  target.value ? `Abrir #${target.value.id}` : ''
);

const link = computed(() =>
  target.value && props.accountId
    ? frontendURL(
        `accounts/${props.accountId}/conversations/${target.value.id}`
      )
    : ''
);
</script>

<template>
  <!-- wrapper com display:contents mantem a raiz sempre presente (vue/no-root-v-if)
       sem introduzir caixa no layout do topBanner -->
  <div class="contents">
    <Banner
      v-if="hasOthers"
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="message"
      :href-link="link"
      :href-link-text="linkText"
    />
  </div>
</template>
